/**
 * Locality directory: pretty name + slug + which shire it belongs to + postcode.
 *
 * Built once at import time from LOCALITY_POSTCODES. Slugs are lowercased
 * and hyphenated so `/locality/margaret-river` is the canonical URL.
 */
import { LOCALITY_POSTCODES } from './locality-postcodes';
import { SHIRES, type ShireId, type Shire } from '../../components/shared/electorate-shires';
import { LOCALITY_BLURBS } from './locality-blurbs';

export interface LocalityEntry {
  /** URL slug, e.g. "margaret-river". */
  slug: string;
  /** Pretty name, e.g. "Margaret River". */
  name: string;
  /** Upper-case name as it appears in the geo data, e.g. "MARGARET RIVER". */
  upperName: string;
  postcode: string;
  shireId: ShireId;
  shireName: string;
  blurb?: string;
}

/** "Shire of Augusta-Margaret River" → "augusta-margaret-river". */
const LGA_PRETTY_TO_ID: Record<string, ShireId> = {
  'Shire of Augusta-Margaret River': 'augusta-margaret-river',
  'Shire of Boyup Brook': 'boyup-brook',
  'Shire of Bridgetown-Greenbushes': 'bridgetown-greenbushes',
  'Shire of Denmark': 'denmark',
  'Shire of Manjimup': 'manjimup',
  'Shire of Nannup': 'nannup',
};

/** Inverse of LGA_NAME_TO_ID in wb-data — for blurb key lookup. */
const ID_TO_LGA_UPPER: Record<ShireId, string> = {
  'augusta-margaret-river': 'AUGUSTA MARGARET RIVER, SHIRE OF',
  'nannup': 'NANNUP, SHIRE OF',
  'manjimup': 'MANJIMUP, SHIRE OF',
  'bridgetown-greenbushes': 'BRIDGETOWN-GREENBUSHES, SHIRE OF',
  'boyup-brook': 'BOYUP BROOK, SHIRE OF',
  'denmark': 'DENMARK, SHIRE OF',
};

export function slugifyLocality(name: string): string {
  return name
    .toLowerCase()
    .replace(/'/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const _entries: LocalityEntry[] = [];
const _bySlug = new Map<string, LocalityEntry>();

for (const row of LOCALITY_POSTCODES) {
  const shireId = LGA_PRETTY_TO_ID[row.lga];
  if (!shireId) continue;
  const slug = slugifyLocality(row.name);
  if (_bySlug.has(slug)) continue; // first wins; duplicates are rare and harmless
  const shire = SHIRES.find((s) => s.id === shireId);
  const upper = row.name.toUpperCase();
  const blurbKey = `${ID_TO_LGA_UPPER[shireId]}|${upper}`;
  const entry: LocalityEntry = {
    slug,
    name: row.name,
    upperName: upper,
    postcode: row.postcode,
    shireId,
    shireName: shire?.name ?? shireId,
    blurb: LOCALITY_BLURBS[blurbKey],
  };
  _entries.push(entry);
  _bySlug.set(slug, entry);
}

_entries.sort((a, b) => a.name.localeCompare(b.name));

export const LOCALITIES: LocalityEntry[] = _entries;

export function localityBySlug(slug: string): LocalityEntry | undefined {
  return _bySlug.get(slug.toLowerCase());
}

export function shireForLocality(entry: LocalityEntry): Shire | undefined {
  return SHIRES.find((s) => s.id === entry.shireId);
}
